// Pure data layer for CPI dashboard queries. Used by the orchestrator in
// CpiReports.tsx via queryClient.fetchQuery, and by chart components via
// useQuery({ enabled: false }) for reactive cache reads. See
// docs/superpowers/specs/2026-05-11-cpi-react-query-cache-design.md.

import axios, { AxiosRequestConfig } from "axios";
import type {
  CpiDashboardData,
  AnalyticsByCustomerResponse,
  EmissionsByTocResponse,
  EmissionsByMonthResponse,
  EmissionsByFuelResponse,
  WeightByModalityResponse,
  FuelConsumptionResponse,
  ErrorDistributionResponse,
  EmissionSourceDistributionResponse,
  IntensityPerTonMonthlyResponse,
  IntensityPerTonKmMonthlyResponse,
  IntensityByModalityResponse,
  IntensityByVehicleResponse,
  IntensityByOrderResponse,
  CpiTripsResponse,
  DataQualityByClientResponse,
} from "pages/cpiReports/types";
import { DEFAULT_CO2_PRICE_PER_TON } from "pages/cpiReports/types";

export interface CpiAdvancedFilters {
  carrierIds: string[];
  fuelTypes: string[];
  modalities: string[];
  emissionSources: string[];
  shipperIds: string[];
  tocs: string[];
}

export interface CpiQueryParams {
  startDate: string;
  endDate: string;
  clientIds: string[];
  carrierIds: string[];
  fuelTypes: string[];
  modalities: string[];
  emissionSources: string[];
  shipperIds: string[];
  tocs: string[];
}

const formatDateToString = (d: Date): string => {
  // Local-date YYYY-MM-DD — matches the existing helper used elsewhere on
  // the page so cache keys align with on-the-wire params.
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const buildCpiQueryParams = (
  startDate: Date,
  endDate: Date,
  clientIds: string[],
  advanced: CpiAdvancedFilters
): CpiQueryParams => ({
  startDate: formatDateToString(startDate),
  endDate: formatDateToString(endDate),
  clientIds: [...clientIds],
  carrierIds: [...advanced.carrierIds],
  fuelTypes: [...advanced.fuelTypes],
  modalities: [...advanced.modalities],
  emissionSources: [...advanced.emissionSources],
  shipperIds: [...advanced.shipperIds],
  tocs: [...advanced.tocs],
});

// ---------------------------------------------------------------------------
// Shared HTTP helpers
// ---------------------------------------------------------------------------

const API_BASE = process.env.REACT_APP_LPI_API_URL;

// Thin axios wrapper that converts a CanceledError (thrown when the AbortSignal
// fires) into a null return instead of an exception. Without this, react-query
// writes the cancel error to the cache and chart components briefly flash
// <ChartErrorState> on revisit before the next fetchQuery overwrites. Any other
// error is re-thrown so react-query's per-query isError state is still set.
const cpiGet = async <T,>(url: string, config: AxiosRequestConfig): Promise<T | null> => {
  try {
    const res = await axios.get<T>(url, config);
    return res.data ?? null;
  } catch (err) {
    if (axios.isCancel(err)) return null;
    throw err;
  }
};

const serializeParams = (params: Record<string, unknown>): string => {
  // Matches the existing serializer used by CpiReports.tsx — arrays become
  // repeated key=val pairs, scalars are straightforward.
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (Array.isArray(v)) v.forEach((item) => usp.append(k, String(item)));
    else if (v !== undefined && v !== null) usp.append(k, String(v));
  });
  return usp.toString();
};

const paramsToAxios = (
  p: CpiQueryParams,
  extra: Record<string, string | number> = {}
): Record<string, string | number | string[]> => {
  const out: Record<string, string | number | string[]> = {
    startDate: p.startDate,
    endDate: p.endDate,
    ...extra,
  };
  if (p.clientIds.length > 0) out.clientId = p.clientIds;
  if (p.carrierIds.length > 0) out.carrierId = p.carrierIds;
  if (p.fuelTypes.length > 0) out.fuelType = p.fuelTypes;
  if (p.modalities.length > 0) out.modality = p.modalities;
  if (p.emissionSources.length > 0) out.emissionSource = p.emissionSources;
  if (p.shipperIds.length > 0) out.shipperId = p.shipperIds;
  if (p.tocs.length > 0) out.tocs = p.tocs;
  return out;
};

export interface CpiAuth {
  jwtToken: string;
  sessionId: string;
}

const authConfig = (auth: CpiAuth): Pick<AxiosRequestConfig, "headers"> => ({
  headers: {
    Authorization: `Session ${auth.jwtToken}`,
    "X-Session-Id": auth.sessionId,
  },
});

// ---------------------------------------------------------------------------
// Query keys + Impl functions — 14 main endpoints
// ---------------------------------------------------------------------------

export const dashboardQueryKey = (p: CpiQueryParams) =>
  ["cpi", "dashboard", p] as const;

export const dashboardImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<CpiDashboardData | null> =>
  cpiGet<CpiDashboardData>(`${API_BASE}/cpi/dashboard`, {
    params: paramsToAxios(p, { co2Price: DEFAULT_CO2_PRICE_PER_TON }),
    ...authConfig(auth),
    paramsSerializer: serializeParams,
    signal,
  });

export const emissionsByCustomerQueryKey = (p: CpiQueryParams) =>
  ["cpi", "emissions-by-customer", p] as const;

export const emissionsByCustomerImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<AnalyticsByCustomerResponse | null> =>
  cpiGet<AnalyticsByCustomerResponse>(
    `${API_BASE}/cpi/analytics/emissions-by-customer`,
    {
      params: paramsToAxios(p, { compareWithPreviousYear: "true" }),
      ...authConfig(auth),
      paramsSerializer: serializeParams,
      signal,
    }
  );

export const emissionsByTocQueryKey = (p: CpiQueryParams) =>
  ["cpi", "emissions-by-toc", p] as const;

export const emissionsByTocImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<EmissionsByTocResponse | null> =>
  cpiGet<EmissionsByTocResponse>(
    `${API_BASE}/cpi/analytics/emissions-by-toc`,
    {
      params: paramsToAxios(p),
      ...authConfig(auth),
      paramsSerializer: serializeParams,
      signal,
    }
  );

export const emissionsByMonthQueryKey = (p: CpiQueryParams) =>
  ["cpi", "emissions-by-month", p] as const;

export const emissionsByMonthImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<EmissionsByMonthResponse | null> =>
  cpiGet<EmissionsByMonthResponse>(
    `${API_BASE}/cpi/analytics/emissions-by-month`,
    {
      params: paramsToAxios(p, { compareWithPreviousYear: "true" }),
      ...authConfig(auth),
      paramsSerializer: serializeParams,
      signal,
    }
  );

export const emissionsByFuelQueryKey = (p: CpiQueryParams) =>
  ["cpi", "emissions-by-fuel", p] as const;

export const emissionsByFuelImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<EmissionsByFuelResponse | null> =>
  cpiGet<EmissionsByFuelResponse>(
    `${API_BASE}/cpi/analytics/emissions-by-fuel`,
    {
      params: paramsToAxios(p),
      ...authConfig(auth),
      paramsSerializer: serializeParams,
      signal,
    }
  );

export const weightByModalityQueryKey = (p: CpiQueryParams) =>
  ["cpi", "weight-by-modality", p] as const;

export const weightByModalityImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<WeightByModalityResponse | null> =>
  cpiGet<WeightByModalityResponse>(
    `${API_BASE}/cpi/analytics/weight-by-modality`,
    {
      params: paramsToAxios(p),
      ...authConfig(auth),
      paramsSerializer: serializeParams,
      signal,
    }
  );

export const shipmentsByCustomerQueryKey = (p: CpiQueryParams) =>
  ["cpi", "shipments-by-customer", p] as const;

export const shipmentsByCustomerImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<AnalyticsByCustomerResponse | null> =>
  cpiGet<AnalyticsByCustomerResponse>(
    `${API_BASE}/cpi/analytics/shipments-by-customer`,
    {
      params: paramsToAxios(p, { compareWithPreviousYear: "true" }),
      ...authConfig(auth),
      paramsSerializer: serializeParams,
      signal,
    }
  );

export const dataQualityByClientQueryKey = (p: CpiQueryParams, sortOrder: 'asc' | 'desc' = 'asc') =>
  ["cpi", "data-quality-by-client", p, sortOrder] as const;

export const dataQualityByClientImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal,
  sortOrder: 'asc' | 'desc' = 'asc'
): Promise<DataQualityByClientResponse | null> =>
  cpiGet<DataQualityByClientResponse>(
    `${API_BASE}/cpi/analytics/data-quality-by-client`,
    {
      params: { startDate: p.startDate, endDate: p.endDate, sortOrder },
      ...authConfig(auth),
      signal,
    }
  );

export const fuelConsumptionQueryKey = (p: CpiQueryParams) =>
  ["cpi", "fuel-consumption", p] as const;

export const fuelConsumptionImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<FuelConsumptionResponse | null> =>
  cpiGet<FuelConsumptionResponse>(
    `${API_BASE}/cpi/analytics/fuel-consumption`,
    {
      params: paramsToAxios(p),
      ...authConfig(auth),
      paramsSerializer: serializeParams,
      signal,
    }
  );

export const errorDistributionQueryKey = (p: CpiQueryParams) =>
  ["cpi", "error-distribution", p] as const;

export const errorDistributionImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<ErrorDistributionResponse | null> =>
  cpiGet<ErrorDistributionResponse>(
    `${API_BASE}/quality/error-distribution`,
    {
      params: paramsToAxios(p),
      ...authConfig(auth),
      paramsSerializer: serializeParams,
      signal,
    }
  );

export const emissionSourceDistributionQueryKey = (p: CpiQueryParams) =>
  ["cpi", "emission-source-distribution", p] as const;

export const emissionSourceDistributionImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<EmissionSourceDistributionResponse | null> =>
  cpiGet<EmissionSourceDistributionResponse>(
    `${API_BASE}/quality/emission-source-distribution`,
    {
      params: paramsToAxios(p),
      ...authConfig(auth),
      paramsSerializer: serializeParams,
      signal,
    }
  );

export const intensityPerTonQueryKey = (p: CpiQueryParams) =>
  ["cpi", "intensity-per-ton-monthly", p] as const;

export const intensityPerTonImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<IntensityPerTonMonthlyResponse | null> =>
  cpiGet<IntensityPerTonMonthlyResponse>(
    `${API_BASE}/cpi/analytics/intensity-per-ton-monthly`,
    {
      params: paramsToAxios(p),
      ...authConfig(auth),
      paramsSerializer: serializeParams,
      signal,
    }
  );

export const intensityPerTonKmQueryKey = (p: CpiQueryParams) =>
  ["cpi", "intensity-per-tonkm-monthly", p] as const;

export const intensityPerTonKmImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<IntensityPerTonKmMonthlyResponse | null> =>
  cpiGet<IntensityPerTonKmMonthlyResponse>(
    `${API_BASE}/cpi/analytics/intensity-per-tonkm-monthly`,
    {
      params: paramsToAxios(p),
      ...authConfig(auth),
      paramsSerializer: serializeParams,
      signal,
    }
  );

export const intensityByModalityQueryKey = (p: CpiQueryParams) =>
  ["cpi", "intensity-per-ton-by-modality", p] as const;

export const intensityByModalityImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<IntensityByModalityResponse | null> =>
  cpiGet<IntensityByModalityResponse>(
    `${API_BASE}/cpi/analytics/intensity-per-ton-by-modality`,
    {
      params: paramsToAxios(p),
      ...authConfig(auth),
      paramsSerializer: serializeParams,
      signal,
    }
  );

export const intensityByVehicleQueryKey = (p: CpiQueryParams) =>
  ["cpi", "intensity-per-tonkm-by-vehicle", p] as const;

export const intensityByVehicleImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<IntensityByVehicleResponse | null> =>
  cpiGet<IntensityByVehicleResponse>(
    `${API_BASE}/cpi/analytics/intensity-per-tonkm-by-vehicle`,
    {
      params: paramsToAxios(p, { limit: 10, offset: 0 }),
      ...authConfig(auth),
      paramsSerializer: serializeParams,
      signal,
    }
  );

export const intensityByOrderQueryKey = (p: CpiQueryParams) =>
  ["cpi", "intensity-per-tonkm-by-order", p] as const;

export const intensityByOrderImpl = async (
  p: CpiQueryParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<IntensityByOrderResponse | null> =>
  cpiGet<IntensityByOrderResponse>(
    `${API_BASE}/cpi/analytics/intensity-per-tonkm-by-order`,
    {
      params: paramsToAxios(p, { limit: 10, offset: 0 }),
      ...authConfig(auth),
      paramsSerializer: serializeParams,
      signal,
    }
  );

// ---------------------------------------------------------------------------
// Trips endpoint — includes pagination in key so each page is cached separately
// ---------------------------------------------------------------------------

export interface CpiTripsPageParams extends CpiQueryParams {
  page: number;
  itemsPerPage: number;
}

export const tripsQueryKey = (p: CpiTripsPageParams) =>
  ["cpi", "trips", p] as const;

export const tripsImpl = async (
  p: CpiTripsPageParams,
  auth: CpiAuth,
  signal?: AbortSignal
): Promise<CpiTripsResponse | null> =>
  cpiGet<CpiTripsResponse>(`${API_BASE}/cpi/trips`, {
    params: paramsToAxios(p, {
      limit: p.itemsPerPage,
      offset: (p.page - 1) * p.itemsPerPage,
    }),
    ...authConfig(auth),
    paramsSerializer: serializeParams,
    signal,
  });
